import type { ServerResponse } from 'http';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import type { AppProps, AppContext } from 'next/app';
import { default as NextApp } from 'next/app';
import Head from 'next/head';
import { ThemeProvider, StylesProvider, jssPreset } from '@material-ui/core/styles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import CssBaseline from '@material-ui/core/CssBaseline';
import { startTransaction } from '@sentry/react';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import type { NodeNextResponse } from 'next/dist/server/base-http/node';
import set from 'lodash/set';
import each from 'lodash/each';
import { create } from 'jss';

import { ErrorBoundary } from '@jerry-serverless/next-sentry';
import { FontsCSS } from '@jerry-serverless/ui-fonts';
import {
  JerryAnalyticsScript,
  JerryReferrerScript,
  GoogleMapsScript,
  TiktokPixelScript,
  TaboolaScript,
  ImpactScript,
  HotjarScript,
  CompanyMetaData,
  TatariPixelScript,
  SnapPixelScript,
  RedditPixelScript,
  PodScribeScript,
} from '@jerry-serverless/ui-analytics';
import { useFacebook } from '@jerry-serverless/ui-analytics/lib/hooks/useFacebook';
import { useFacebookPixel } from '@jerry-serverless/ui-analytics/lib/hooks/useFacebookPixel';
import { useGoogleTagManager } from '@jerry-serverless/ui-analytics/lib/hooks/useGoogleTagManager';
import { registerPodScribeReady } from '@jerry-serverless/ui-analytics/lib/scripts/podscribe';

import { createTheme, createDefaultTheme } from '../styles/theme';
import type { AppData } from '../types/app_data';
import { AppStepSection } from '../types/app_data';
import { getAppData } from '../utils/app_data';
import { identifyUser, trackEvent, trackPixelLanding } from '../utils/analytics';
import { resendPendingPodScribeEvents } from '../utils/analytics/podscribe';
import { Direction } from '../steps/types/step';
import type { StepManagerDataType } from '../steps/types/step_manager';
import { redirectOnLoad, StepManagerContext } from '../steps';
import { AppContainer } from '../components/app_container/AppContainer';
import { getAppContainerProps } from '../utils/partner';
import { useTrackTranslation } from '../hooks/useTrackTranslation';
import { createGrowthBookFromAppData } from '../utils/analytics/growthbook';
import {
  StepNavContext,
  useInitStepNavContextData,
} from '../components/step_container/components/step_nav/StepNavContext';
import { useTrackIPAddress } from '../hooks/useTrackIPAddress';
import { configureAmplify } from '../amplify/configure';
import { useGetIPLocation } from '../utils/ipLocation';
import { useRedirectWebAppLoggedInUser } from '../utils/webapp';
import { getSiteUrl } from '../utils/constants';

const nonSignupPages = ['/2024-year-in-review', '/notification-settings', '/login/mobile-admin-login'];

try {
  Sentry.init({
    dsn: 'https://8f9ac78cdcd546a4af4534bc67a410f4@sentry.ing.getjerry.com/11',
    integrations: [new Integrations.BrowserTracing()],
    environment: process.env.NEXT_PUBLIC_STAGE,
    tracesSampleRate: 0.2,
  });
} catch (error) {
  console.error(`Sentry init for Signup failed`, error);
}

/**
 * App component
 */
function App(props: AppProps & { appData: AppData }): JSX.Element {
  const { Component, pageProps, router } = props;

  const isNonSignupPage = nonSignupPages.includes(router.route);

  // Step manager state.
  const [stepManagerData, setStepManagerData] = useState<StepManagerDataType>({
    appData: props.appData,
    stepStack: [],
    direction: Direction.Right,
    changingStepSection: false,
  });

  // Create or get directly the singleton GrowthBook instance
  const growthbook = createGrowthBookFromAppData(stepManagerData.appData);

  useEffect(() => {
    const initGrowthbook = async () => {
      await growthbook.loadFeatures().catch((e) => console.error(e));
    };
    void initGrowthbook();
  }, [growthbook]);

  // Get theme.
  const theme = useMemo(() => {
    return createTheme(stepManagerData.appData.flowType || stepManagerData.appData.partner?.partner);
  }, [stepManagerData.appData.partner?.partner, stepManagerData.appData.flowType]);

  const defaultTheme = useMemo(() => {
    return createDefaultTheme(stepManagerData.appData.flowType || stepManagerData.appData.partner?.partner);
  }, [stepManagerData.appData.partner?.partner, stepManagerData.appData.flowType]);

  // Check if nav bar should not be shown.
  const noShowNav = useMemo(
    () => stepManagerData.appData.mobileAppView || stepManagerData.appData.stepSection === AppStepSection.Finished,
    [stepManagerData.appData]
  );

  // Grab app container props.
  // Using the page specific app container props compute the app container props using partner data.
  const appContainerProps = useMemo(
    () =>
      getAppContainerProps(
        stepManagerData.appData,
        { ...pageProps?.appContainerProps, route: router.route },
        pageProps?.forceAppContainerProps
      ),
    [stepManagerData, pageProps, router.route]
  );

  /**
   * On app load on client side:
   */
  useEffect(() => {
    // Handler for route change complete.
    const handleRouteChangeComplete = () => {
      // Scroll to top of page after route change.
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 10);
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  useEffect(() => {
    const page = () => {
      const { pathname } = router;
      const eventLabel = pathname.replace(/\//g, '-');

      void trackEvent('PageView', { eventLabel });
    };
    page();

    router.events.on('routeChangeComplete', page);
    return () => {
      router.events.off('routeChangeComplete', page);
    };
  }, [router]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // configure the Amplify environment
      configureAmplify();
    }
  }, []);

  /**
   * Get IP location and store in sessionStorage.
   */
  useGetIPLocation(stepManagerData.appData.clientIP);

  /**
   * Send analytics events.
   */
  useEffect(() => {
    let signupStartEventLabel: 'New' | 'Validated' | 'Signed' | 'Returning' = 'New';

    // Identify user.
    // NOTE: This should be located in a useEffect with dependency on `stepManagerData`
    // such that when the data is updated from mutations, the `identifyUser` is automatically
    // called here from one place. Right now we call it from multiple places.
    if (props.appData?.id) {
      // All the supplied values should be non-nil if `id` is present.
      // `||` with default values to keep Typescript happy.
      void identifyUser({
        id: props.appData.id,
        name: props.appData.name || '',
        phone: props.appData.phone || '',
        email: props.appData.email,
        address: props.appData.address,
        mlCoverageEnabled: props.appData.mlCoverageEnabled || false,
        isOnboardingLite: props.appData.isOnboardingLite || false,
        enableLDU: props.appData.enableLDU || false,
      });

      if (props.appData.signupParams?.validated) {
        signupStartEventLabel = 'Validated';
      } else if (props.appData.isSigned) {
        signupStartEventLabel = 'Signed';
      } else {
        signupStartEventLabel = 'Returning';
      }
    }

    // Send signup 'start' event.
    void trackEvent('CreateAccountStart', {
      eventLabel: signupStartEventLabel,
      eventMetadata: { version: 'serverless' },
    });

    void trackEvent('CreateAccountPartner', {
      eventLabel: props.appData?.partner?.partner || 'None',
    });
  }, [props.appData]);

  useRedirectWebAppLoggedInUser(props.appData);

  useFacebook();
  useFacebookPixel();
  useGoogleTagManager();

  useTrackIPAddress();

  // Track page translation detection
  useTrackTranslation();

  // Handle podscribe pixel events
  const podScribeInit = useCallback(async () => {
    void trackEvent('CreateAccountPobScribePixel', { eventLabel: 'View' }); // 'Signup | PodScribe | View'
    void trackPixelLanding(props.appData?.sessionId);
    resendPendingPodScribeEvents((eventLabel) => {
      void trackEvent('CreateAccountPobScribePixel', { eventLabel }); // 'Signup | PodScribe | signup' or 'purchase'
    });
  }, [props.appData?.sessionId]);
  registerPodScribeReady(podScribeInit);

  const stepNavContextData = useInitStepNavContextData(stepManagerData.appData);

  const jss = useMemo(() => {
    const insertionPoint = typeof document !== 'undefined' ? document.getElementById('jss-insertion-point') : null;
    if (insertionPoint) {
      return create({
        ...jssPreset(),
        insertionPoint,
      });
    }
    return create(jssPreset());
  }, []);

  return (
    <ErrorBoundary>
      <Head>
        <title>Personal Insurance Shopper - Jerry</title>
        <meta name="robots" content="noindex" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />
        <meta property="og:title" content="Personal Insurance Shopper - Jerry" />
        <meta
          name="description"
          content="78% of drivers are overpaying for car insurance. Make sure you’re not one of them."
        />
        <meta property="og:image" content={`${getSiteUrl()}/imgs/favicon-256x256.png`} />
        <meta property="fb:app_id" content={process.env.NEXT_PUBLIC_FB_APP_ID} />
        <meta property="og:image" content={`${getSiteUrl()}/favicon.png`} />
        <link rel="icon" href="/favicon.png" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(CompanyMetaData) }} />
      </Head>
      <StylesProvider jss={jss}>
        <ThemeProvider theme={theme}>
          <StyledThemeProvider theme={defaultTheme}>
            <CssBaseline />
            <FontsCSS />

            <GrowthBookProvider growthbook={growthbook}>
              <StepManagerContext.Provider value={{ data: stepManagerData, setData: setStepManagerData }}>
                <StepNavContext.Provider value={stepNavContextData}>
                  {isNonSignupPage ? (
                    <Component {...pageProps} />
                  ) : (
                    <AppContainer
                      route={router.route}
                      direction={stepManagerData.direction}
                      isLoading={stepManagerData.changingStepSection}
                      noShowNav={noShowNav}
                      appData={stepManagerData.appData}
                      {...appContainerProps}
                    >
                      <Component {...pageProps} />
                    </AppContainer>
                  )}
                </StepNavContext.Provider>
              </StepManagerContext.Provider>
            </GrowthBookProvider>

            <JerryAnalyticsScript />
            <JerryReferrerScript />
            <GoogleMapsScript />
            <TiktokPixelScript />
            <RedditPixelScript />
            <TatariPixelScript />
            <SnapPixelScript />
            <TaboolaScript />
            <HotjarScript />
            <ImpactScript />
            <PodScribeScript />
          </StyledThemeProvider>
        </ThemeProvider>
      </StylesProvider>
    </ErrorBoundary>
  );
}

const isServerResponse = (response: NodeNextResponse | ServerResponse | undefined): response is ServerResponse =>
  (response as ServerResponse)?.writeHead !== undefined;

/**
 * Perform initiate routing on app load.
 */
App.getInitialProps = async (ctx: AppContext) => {
  const transaction = startTransaction({
    name: `App_getInitialProps_${process.env.NEXT_PUBLIC_STAGE}`,
    op: 'task',
    metadata: {
      request: ctx.ctx.req,
      requestPath: ctx.ctx.req?.url,
    },
  });

  const isServer = typeof window === 'undefined';

  // eslint-disable-next-line no-console
  console.log(
    '[getInitialProps url]',
    ctx.ctx.pathname,
    ctx.ctx.req?.url,
    isServer,
    ctx.ctx.req?.headers?.['x-request-id']
  );

  let appData: AppData = { stepSection: AppStepSection.CreateAccount };
  const res = ctx.ctx.res as NodeNextResponse | ServerResponse | undefined;
  // Should only run on server
  if (isServer) {
    // Healthcheck route.
    if (ctx.ctx.asPath === '/health' && res) {
      if (isServerResponse(res)) {
        renderHealthCheckPage(res);
      } else {
        res.statusCode = 200;
        renderHealthCheckPage(res.originalResponse);
        res.send();
      }
      return { appData };
    }

    // // --- For local dev ignore ---
    // if (ctx.ctx.pathname === '/_error' && res) {
    //   if (isServerResponse(res)) {
    //     res.writeHead(404);
    //     res.end();
    //   } else {
    //     res.statusCode = 404;
    //     res.send();
    //   }
    //   return { appData };
    // }

    const isNonSignupPage = [...nonSignupPages].includes(ctx.ctx.pathname);

    if (!isNonSignupPage) {
      appData = await getAppData(ctx);
      const redirPath = await redirectOnLoad(ctx.ctx, appData);
      if (redirPath && res) {
        if (isServerResponse(res)) {
          res.writeHead(307, { Location: `${redirPath}` });
          res.end();
        } else {
          res.statusCode = 307;
          res.setHeader('Location', `${redirPath}`);
          res.send();
        }
        return { appData };
      }
      /**
       * Add the `appData` property to req, then access it in `getServerSideProps`
       */
      if (ctx.ctx.req) {
        set(ctx.ctx.req, 'appData', appData);
      }
    }
  }

  const props = await NextApp.getInitialProps(ctx);

  transaction.finish();

  return { ...props, appData };
};

const renderHealthCheckPage = (res: ServerResponse) => {
  res.writeHead(200, { Location: `/health` });
  res.write('<b>status: OK</b><br><br>');
  res.write('<b>Environment</b><br>');
  const envKeys: string[] = [
    'NEXT_PUBLIC_STAGE',
    'NEXT_PUBLIC_HOST',
    'NEXT_PUBLIC_API_URL',
    'NEXT_PUBLIC_REFI_API_URL',
    'NEXT_PUBLIC_QUOTING_API_URL',
    'NEXT_PUBLIC_GARAGE_API_URL',
    'NEXT_PUBLIC_JA_URL',
    'NEXT_PUBLIC_GROWTHBOOK_API_HOST',
  ];
  res.write('<table>');
  each(envKeys, (key: string) => {
    res.write(`<tr><td>${key}</td><td>${process.env[key]}</td></tr>`);
  });
  res.write('</table>');

  res.write('<br><b>Proxy</b><br>');
  res.write('<table>');
  const proxyConfig = JSON.parse(`${process.env['proxyConfig']}` || '{}');
  each(proxyConfig, (val: { target: string }, url: string) => {
    res.write(`<tr><td>${url}</td><td>${val.target}</td></tr>`);
  });
  res.write('</table>');
  res.write('\n');
  res.end();
};
export default App;
